import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import { 
  Users, 
  Store, 
  ShoppingCart, 
  IndianRupee,
  MessageSquare,
  TrendingUp,
  Loader2,
  CalendarDays,
  Wallet,
  PackageCheck,
  Boxes,
  Factory,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const StatCard = ({ title, value, icon: Icon, trend, color, to }) => {
  const content = (
    <Card className="bg-white border-[#E5E5E5] hover:shadow-md transition-all duration-300 hover:-translate-y-1">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[#A8A8A8] font-medium">{title}</p>
            <p className="text-3xl font-bold text-[#2D2420] mt-2">{value}</p>
            {trend && (
              <div className="flex items-center gap-1 mt-2 text-sm text-[#4A7c59]">
                <TrendingUp className="w-4 h-4" />
                <span>{trend}</span>
              </div>
            )}
          </div>
          <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", color)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (to) {
    return <Link to={to} className="block">{content}</Link>;
  }

  return content;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    packed: 'bg-blue-100 text-blue-800',
    on_the_way: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    new: 'bg-[#C97A5E]/10 text-[#C97A5E]',
    contacted: 'bg-blue-100 text-blue-800',
    qualified: 'bg-green-100 text-green-800',
    converted: 'bg-emerald-100 text-emerald-800',
    closed: 'bg-gray-100 text-gray-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const QuickAction = ({ to, label, helper }) => (
  <Link to={to} className="group rounded-2xl border border-[#E5E5E5] bg-[#FAFAF9] p-4 hover:border-[#D8C3B6] hover:bg-white transition-colors">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-[#2D2420]">{label}</p>
        <p className="text-xs text-[#A8A8A8] mt-1">{helper}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-[#C97A5E] group-hover:translate-x-0.5 transition-transform" />
    </div>
  </Link>
);

const ControlMetric = ({ title, value, helper, tone = 'default' }) => {
  const tones = {
    default: 'bg-white border-[#E5E5E5]',
    warm: 'bg-[#FFF7F2] border-[#F0D8CB]',
    cool: 'bg-[#F3FBF9] border-[#CFE6E0]',
    danger: 'bg-[#FFF5F5] border-[#F3D0D0]',
  };
  return (
    <Card className={tones[tone] || tones.default}>
      <CardContent className="p-4">
        <p className="text-xs text-[#A8A8A8]">{title}</p>
        <p className="text-2xl font-bold text-[#2D2420] mt-1">{value}</p>
        {helper ? <p className="text-xs text-[#5C4033] mt-2">{helper}</p> : null}
      </CardContent>
    </Card>
  );
};

const mapDashboardPeriodToFinancialPeriod = (period) => {
  if (period === 'last_month' || period === 'current_month' || period === 'custom') return 'month';
  if (period === 'fy_to_date') return 'year';
  return 'month';
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [businessHub, setBusinessHub] = useState(null);
  const [inventoryForecast, setInventoryForecast] = useState(null);
  const [alertHub, setAlertHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('current_month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [periodLoading, setPeriodLoading] = useState(false);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastLookbackDays, setForecastLookbackDays] = useState('60');
  const [forecastDays, setForecastDays] = useState('30');
  const [forecastSafetyBuffer, setForecastSafetyBuffer] = useState('10');
  const [alertStatusFilter, setAlertStatusFilter] = useState('open');
  const [alertLoading, setAlertLoading] = useState(false);

  useEffect(() => {
    fetchDashboard({ period: 'current_month' });
    fetchBusinessHub('current_month');
    fetchInventoryForecast({ lookback_days: 60, forecast_days: 30, safety_buffer_pct: 10, limit: 12 });
    fetchAlertHub({ status: 'open', limit: 20 });
  }, []);

  const fetchDashboard = async (params = {}) => {
    try {
      const response = await adminApi.getDashboard(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessHub = async (dashboardPeriod = period) => {
    try {
      const financialPeriod = mapDashboardPeriodToFinancialPeriod(dashboardPeriod);
      const [
        financialRes,
        warehousesRes,
        sessionsRes,
        packingMaterialsRes,
        packingMaterialLotsRes,
      ] = await Promise.all([
        adminApi.getFinancialSummary(financialPeriod),
        adminApi.getWarehouses(),
        adminApi.getPackingSessions({ page_size: 200 }),
        adminApi.getPackingMaterialItems(),
        adminApi.getPackingMaterialStockLots(),
      ]);

      const warehouses = warehousesRes.data || [];
      const packingWarehouse = warehouses.find((warehouse) => warehouse.tag === 'PACKING' || warehouse.warehouse_type === 'PACKING') ||
        warehouses.find((warehouse) => String(warehouse.name || '').toLowerCase().includes('pack'));
      const mainWarehouse = warehouses.find((warehouse) => warehouse.tag === 'MAIN') ||
        warehouses.find((warehouse) => String(warehouse.name || '').toLowerCase().includes('main'));

      const [packingRawRes, mainRawRes, packingFinishedRes] = await Promise.all([
        packingWarehouse ? adminApi.getRawStockLots({ warehouse_id: packingWarehouse.id }) : Promise.resolve({ data: [] }),
        mainWarehouse ? adminApi.getRawStockLots({ warehouse_id: mainWarehouse.id }) : Promise.resolve({ data: [] }),
        packingWarehouse ? adminApi.getWarehouseStock(packingWarehouse.id) : Promise.resolve({ data: [] }),
      ]);

      const sessions = sessionsRes.data?.items || [];
      const packingMaterialItems = packingMaterialsRes.data || [];
      const packingMaterialLots = packingMaterialLotsRes.data || [];
      const packingFinishedStock = packingFinishedRes.data || [];
      const packingRawLots = packingRawRes.data || [];
      const mainRawLots = mainRawRes.data || [];

      const pendingSessions = sessions.filter((session) => !['completed', 'cancelled'].includes(String(session.status || '').toLowerCase()));
      const readyToTransferRows = packingFinishedStock.filter((row) => Number(row.available || 0) > 0);
      const readyToTransferQty = readyToTransferRows.reduce((sum, row) => sum + Number(row.available || 0), 0);

      const rawByName = new Map();
      [...mainRawLots, ...packingRawLots].forEach((lot) => {
        const key = lot.raw_item_name || 'Unknown';
        rawByName.set(key, (rawByName.get(key) || 0) + Number(lot.kg_total || 0));
      });
      const rawShortages = Array.from(rawByName.entries())
        .filter(([, total]) => total <= 0.000001)
        .map(([name]) => name);

      const packingMaterialTotals = new Map();
      packingMaterialLots.forEach((lot) => {
        const itemId = lot.packing_material_item_id;
        packingMaterialTotals.set(itemId, (packingMaterialTotals.get(itemId) || 0) + Number(lot.qty_total || 0));
      });
      const outOfStockPackingMaterials = packingMaterialItems
        .filter((item) => item.is_active !== false)
        .filter((item) => Number(packingMaterialTotals.get(item.id) || 0) <= 0.000001);

      const financial = financialRes.data || null;
      setFinancialSummary(financial);
      setBusinessHub({
        pendingSessions,
        readyToTransferRows,
        readyToTransferQty,
        packingMaterialItems,
        outOfStockPackingMaterials,
        rawShortages,
        packingWarehouseName: packingWarehouse?.name || 'Packing Warehouse',
        mainWarehouseName: mainWarehouse?.name || 'Main Warehouse',
      });
    } catch (error) {
      console.error('Failed to fetch business hub:', error);
      setBusinessHub(null);
      setFinancialSummary(null);
    }
  };

  const fetchInventoryForecast = async (params = {}) => {
    setForecastLoading(true);
    try {
      const response = await adminApi.getInventoryForecast(params);
      setInventoryForecast(response.data || null);
    } catch (error) {
      console.error('Failed to fetch inventory forecast:', error);
      setInventoryForecast(null);
    } finally {
      setForecastLoading(false);
    }
  };

  const fetchAlertHub = async (params = {}) => {
    setAlertLoading(true);
    try {
      const response = await adminApi.getAlertingHub(params);
      setAlertHub(response.data || null);
    } catch (error) {
      console.error('Failed to fetch alerting hub:', error);
      setAlertHub(null);
    } finally {
      setAlertLoading(false);
    }
  };

  const applyPeriodFilter = async () => {
    const params = { period };
    if (period === 'custom') {
      if (!dateFrom || !dateTo) {
        return;
      }
      params.date_from = dateFrom;
      params.date_to = dateTo;
    }

    setPeriodLoading(true);
    try {
      await fetchDashboard(params);
      await fetchBusinessHub(period);
    } finally {
      setPeriodLoading(false);
    }
  };

  const applyForecastFilter = async () => {
    const lookback = Number(forecastLookbackDays || 60);
    const horizon = Number(forecastDays || 30);
    const safety = Number(forecastSafetyBuffer || 10);
    await fetchInventoryForecast({
      lookback_days: Number.isFinite(lookback) ? lookback : 60,
      forecast_days: Number.isFinite(horizon) ? horizon : 30,
      safety_buffer_pct: Number.isFinite(safety) ? safety : 10,
      limit: 12,
    });
  };

  const applyAlertFilter = async () => {
    await fetchAlertHub({ status: alertStatusFilter, limit: 20 });
  };

  const updateAlertStatus = async (alertKey, status) => {
    try {
      await adminApi.updateAlertingStatus({ alert_key: alertKey, status });
      await fetchAlertHub({ status: alertStatusFilter, limit: 20 });
    } catch (error) {
      console.error('Failed to update alert status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Business Control Center</h1>
        <p className="text-[#A8A8A8] mt-1">Finance, collections, warehouse flow, and execution queues in one place</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <Card className="bg-[linear-gradient(135deg,#FFF7F0_0%,#FAFAF9_55%,#F3FBF9_100%)] border-[#E7D7CD]">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#5C4033] uppercase tracking-[0.18em]">Business Hub</p>
                <h2 className="text-2xl font-bold text-[#2D2420] mt-2">Run finance and operations from one screen</h2>
                <p className="text-sm text-[#6F625B] mt-2">Use this hub to monitor liquidity, due collections, packing progress, and stock readiness before dispatch.</p>
              </div>
              <div className="hidden md:flex items-center justify-center w-14 h-14 rounded-2xl bg-[#C97A5E]">
                <Factory className="w-7 h-7 text-white" />
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <ControlMetric title="Cash + Bank" value={formatCurrency((financialSummary?.cash_in_hand || 0) + (financialSummary?.in_account || 0))} helper="Liquid business funds" tone="warm" />
              <ControlMetric title="Receivables" value={formatCurrency(financialSummary?.payment_to_be_received || 0)} helper="Customer collections pending" tone="danger" />
              <ControlMetric title="Payables" value={formatCurrency(financialSummary?.payment_to_be_done || 0)} helper="Vendor and outgoing dues" />
              <ControlMetric title="Pending Sessions" value={formatNumber(businessHub?.pendingSessions?.length || 0)} helper="Packing work still open" tone="cool" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="p-4 grid grid-cols-1 gap-3">
            <QuickAction to="/payments" label="Record Receipt" helper="Capture customer collections and credit note settlements" />
            <QuickAction to="/expenses" label="Office Expense / Income" helper="Post rent, electricity, and office cash movements" />
            <QuickAction to="/warehouse/packing-sessions" label="Create Packing Session" helper="Assign staff and start production work" />
            <QuickAction to="/warehouse/packing-materials" label="Manage Packing Materials" helper="Receive, transfer, and watch consumables" />
            <QuickAction to="/ledger" label="Open Ledger" helper="Audit posted entries across receipts, expenses, and vendors" />
            <QuickAction to="/warehouse/overview" label="Warehouse View" helper="Check raw, packing, and sale-ready stock status" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="bg-white border-[#E5E5E5] xl:col-span-1">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2"><Wallet className="w-5 h-5 text-[#C97A5E]" />Finance Pulse</CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-[#E5E5E5] p-4 bg-[#FAFAF9]">
                <p className="text-xs text-[#A8A8A8]">Cash in Hand</p>
                <p className="text-xl font-bold text-[#2D2420] mt-1">{formatCurrency(financialSummary?.cash_in_hand || 0)}</p>
              </div>
              <div className="rounded-xl border border-[#E5E5E5] p-4 bg-[#FAFAF9]">
                <p className="text-xs text-[#A8A8A8]">In Account</p>
                <p className="text-xl font-bold text-[#2D2420] mt-1">{formatCurrency(financialSummary?.in_account || 0)}</p>
              </div>
              <div className="rounded-xl border border-[#F3D0D0] p-4 bg-[#FFF5F5]">
                <p className="text-xs text-red-600">Receivables</p>
                <p className="text-xl font-bold text-red-700 mt-1">{formatCurrency(financialSummary?.payment_to_be_received || 0)}</p>
              </div>
              <div className="rounded-xl border border-[#F0D8CB] p-4 bg-[#FFF7F2]">
                <p className="text-xs text-[#C97A5E]">Payables</p>
                <p className="text-xl font-bold text-[#C97A5E] mt-1">{formatCurrency(financialSummary?.payment_to_be_done || 0)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-[#E5E5E5] p-4">
              <p className="text-sm font-semibold text-[#2D2420]">Profit Snapshot</p>
              <div className="flex items-end justify-between mt-2">
                <div>
                  <p className="text-xs text-[#A8A8A8]">Sales Profit</p>
                  <p className="text-2xl font-bold text-[#4A7C59]">{formatCurrency(financialSummary?.sales_profit_total || 0)}</p>
                </div>
                <Badge className="bg-[#E7F3EE] text-[#2E6B4B]">{Number(financialSummary?.sales_profit_margin_percentage || 0).toFixed(2)}% margin</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5] xl:col-span-1">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2"><Factory className="w-5 h-5 text-[#2A9D8F]" />Packing Control</CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-[#CFE6E0] bg-[#F3FBF9] p-4">
                <p className="text-xs text-[#4A7C59]">Pending Sessions</p>
                <p className="text-2xl font-bold text-[#2D2420] mt-1">{formatNumber(businessHub?.pendingSessions?.length || 0)}</p>
              </div>
              <div className="rounded-xl border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                <p className="text-xs text-[#A8A8A8]">Ready To Transfer</p>
                <p className="text-2xl font-bold text-[#2D2420] mt-1">{formatNumber(businessHub?.readyToTransferQty || 0)}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2D2420] mb-2">Open Packing Sessions</p>
              <div className="space-y-2">
                {(businessHub?.pendingSessions || []).slice(0, 5).map((session) => (
                  <div key={session.id} className="flex items-start justify-between gap-3 rounded-lg border border-[#EDE7E3] p-3">
                    <div>
                      <p className="text-sm font-medium text-[#2D2420]">{session.session_ref}</p>
                      <p className="text-xs text-[#A8A8A8]">{session.session_name || session.order_ref || 'Packing session'}</p>
                    </div>
                    <Badge className={cn('text-xs', getStatusColor(session.status))}>{session.status}</Badge>
                  </div>
                ))}
                {!(businessHub?.pendingSessions || []).length && <p className="text-sm text-[#A8A8A8]">No open packing work.</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5] xl:col-span-1">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420] flex items-center gap-2"><Boxes className="w-5 h-5 text-[#5C4033]" />Inventory Watch</CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                <p className="text-xs text-[#A8A8A8]">Sale SKU Alerts</p>
                <p className="text-2xl font-bold text-[#2D2420] mt-1">{formatNumber(data?.smart_alerts?.low_stock_count || 0)}</p>
              </div>
              <div className="rounded-xl border border-[#F3D0D0] bg-[#FFF5F5] p-4">
                <p className="text-xs text-red-600">Materials Out</p>
                <p className="text-2xl font-bold text-red-700 mt-1">{formatNumber(businessHub?.outOfStockPackingMaterials?.length || 0)}</p>
              </div>
              <div className="rounded-xl border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                <p className="text-xs text-[#A8A8A8]">Packing FG Lines</p>
                <p className="text-2xl font-bold text-[#2D2420] mt-1">{formatNumber(businessHub?.readyToTransferRows?.length || 0)}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2D2420] mb-2">Packing Material Attention</p>
              <div className="space-y-2">
                {(businessHub?.outOfStockPackingMaterials || []).slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center justify-between rounded-lg border border-[#F3D0D0] bg-[#FFF5F5] px-3 py-2">
                    <span className="text-sm text-[#2D2420]">{item.name}</span>
                    <Badge className="bg-red-100 text-red-700">Out of stock</Badge>
                  </div>
                ))}
                {!(businessHub?.outOfStockPackingMaterials || []).length && <p className="text-sm text-[#A8A8A8]">All active packing materials currently have stock.</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-end lg:justify-between">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-[#A8A8A8] mb-1">Period</p>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_month">Current Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="fy_to_date">FY to Date</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {period === 'custom' ? (
              <>
                <div>
                  <p className="text-xs text-[#A8A8A8] mb-1">From</p>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <p className="text-xs text-[#A8A8A8] mb-1">To</p>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </>
            ) : (
              <div className="md:col-span-2 flex items-center gap-2 text-sm text-[#5C4033]">
                <CalendarDays className="w-4 h-4" />
                <span>{data?.period_label || 'Current Month'}</span>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={applyPeriodFilter}
              disabled={periodLoading || (period === 'custom' && (!dateFrom || !dateTo))}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {periodLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid - Bento Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Customers" 
          value={data?.total_customers || 0} 
          icon={Store}
          color="bg-[#5C4033]"
          to="/customers"
        />
        <StatCard 
          title="New Orders" 
          value={data?.pending_orders || 0} 
          icon={ShoppingCart}
          color="bg-[#F4A261]"
          to="/orders?status=pending"
        />
        <StatCard 
            title={`Delivered${data?.period_label ? ` (${data.period_label})` : ''}`} 
          value={data?.delivered_this_month || 0} 
          icon={ShoppingCart}
          color="bg-[#2A9D8F]"
          to="/orders?status=delivered"
        />
        <StatCard 
            title={`Orders${data?.period_label ? ` (${data.period_label})` : ''}`} 
          value={data?.orders_this_month || 0} 
          icon={ShoppingCart}
          color="bg-[#C97A5E]"
          to="/orders"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard 
          title={`Payments Collected${data?.period_label ? ` (${data.period_label})` : ''}`} 
          value={formatCurrency(data?.payments_collected_this_month || 0)} 
          icon={IndianRupee}
          color="bg-[#4A7c59]"
          to="/payments"
        />
        <StatCard 
          title="Outstanding Credit" 
          value={formatCurrency(data?.outstanding_credit || 0)} 
          icon={TrendingUp}
          color="bg-[#E76F51]"
          to="/customers"
        />
        <StatCard 
          title="Sales Staff" 
          value={data?.total_sales_staff || 0} 
          icon={Users}
          color="bg-[#2A9D8F]"
          to="/sales-staff"
        />
        <StatCard 
          title="Total Products" 
          value={data?.total_products || 0} 
          icon={ShoppingCart}
          color="bg-[#D2B48C]"
          to="/products"
        />
        <StatCard 
          title="New Leads" 
          value={data?.new_leads || 0} 
          icon={MessageSquare}
          color="bg-[#C97A5E]"
          to="/leads"
        />
      </div>

      {/* 1) Sales Funnel */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="border-b border-[#E5E5E5]">
          <CardTitle className="text-lg text-[#2D2420]">Sales Funnel ({data?.period_label || 'Current Month'})</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-[#FAFAF9]">
              <p className="text-xs text-[#A8A8A8]">Leads Captured</p>
              <p className="text-2xl font-bold text-[#2D2420]">{data?.sales_funnel?.leads_captured || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-[#FAFAF9]">
              <p className="text-xs text-[#A8A8A8]">Qualified Leads</p>
              <p className="text-2xl font-bold text-[#2D2420]">{data?.sales_funnel?.qualified_leads || 0}</p>
              <p className="text-xs text-[#4A7c59]">{formatPercent(data?.sales_funnel?.lead_to_qualified_rate)}</p>
            </div>
            <div className="p-4 rounded-lg bg-[#FAFAF9]">
              <p className="text-xs text-[#A8A8A8]">Orders Created</p>
              <p className="text-2xl font-bold text-[#2D2420]">{data?.sales_funnel?.orders_created || 0}</p>
              <p className="text-xs text-[#4A7c59]">{formatPercent(data?.sales_funnel?.qualified_to_order_rate)}</p>
            </div>
            <div className="p-4 rounded-lg bg-[#FAFAF9]">
              <p className="text-xs text-[#A8A8A8]">Paid Orders</p>
              <p className="text-2xl font-bold text-[#2D2420]">{data?.sales_funnel?.paid_orders || 0}</p>
              <p className="text-xs text-[#4A7c59]">{formatPercent(data?.sales_funnel?.order_to_paid_rate)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2) Smart Alerts + 5) Collections Workflow */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Smart Alerts</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                <p className="text-xs text-red-600">Overdue Payments</p>
                <p className="text-xl font-bold text-red-700">{data?.smart_alerts?.overdue_payments_count || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                <p className="text-xs text-amber-700">Low Stock</p>
                <p className="text-xl font-bold text-amber-800">{data?.smart_alerts?.low_stock_count || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-xs text-blue-700">Stale Leads</p>
                <p className="text-xl font-bold text-blue-800">{data?.smart_alerts?.stale_leads_count || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-100">
                <p className="text-xs text-purple-700">Delivery Issues</p>
                <p className="text-xl font-bold text-purple-800">{data?.smart_alerts?.delivery_issues_count || 0}</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-[#2D2420] mb-2">Top Overdue</p>
              <div className="space-y-2">
                {(data?.smart_alerts?.overdue_payments || []).slice(0, 4).map((item) => (
                  <div key={item.order_id} className="flex justify-between text-sm border-b border-[#F1EFEE] pb-2">
                    <span className="text-[#5C4033]">{item.order_id} • {item.customer_name}</span>
                    <span className="font-semibold text-red-700">{formatCurrency(item.balance)}</span>
                  </div>
                ))}
                {!(data?.smart_alerts?.overdue_payments || []).length && (
                  <p className="text-sm text-[#A8A8A8]">No overdue payment alerts.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Collections Workflow</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-[#FAFAF9]">
                <p className="text-xs text-[#A8A8A8]">Total Outstanding</p>
                <p className="text-xl font-bold text-[#2D2420]">{formatCurrency(data?.collections_workflow?.total_outstanding || 0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#FAFAF9]">
                <p className="text-xs text-[#A8A8A8]">Total Overdue</p>
                <p className="text-xl font-bold text-red-700">{formatCurrency(data?.collections_workflow?.total_overdue || 0)}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Badge className="bg-slate-100 text-slate-700 justify-center">1-7d: {data?.collections_workflow?.bucket_counts?.overdue_1_7 || 0}</Badge>
              <Badge className="bg-amber-100 text-amber-800 justify-center">8-15d: {data?.collections_workflow?.bucket_counts?.overdue_8_15 || 0}</Badge>
              <Badge className="bg-red-100 text-red-700 justify-center">31+d: {data?.collections_workflow?.bucket_counts?.overdue_31_plus || 0}</Badge>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2D2420] mb-2">Follow-up Queue</p>
              <div className="space-y-2">
                {(data?.collections_workflow?.follow_up_queue || []).slice(0, 4).map((item) => (
                  <div key={item.order_id} className="flex justify-between text-sm border-b border-[#F1EFEE] pb-2">
                    <span className="text-[#5C4033]">{item.customer_name} ({item.days_overdue}d)</span>
                    <span className="font-semibold text-[#2D2420]">{formatCurrency(item.balance)}</span>
                  </div>
                ))}
                {!(data?.collections_workflow?.follow_up_queue || []).length && (
                  <p className="text-sm text-[#A8A8A8]">No pending follow-ups.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerting Hub */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="border-b border-[#E5E5E5]">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle className="text-lg text-[#2D2420]">Alerting Hub</CardTitle>
              <Link to="/reports/alerts" className="text-sm text-[#C97A5E] hover:text-[#B06850]">Open full view</Link>
            </div>
            <div className="flex gap-2 items-center">
              <Select value={alertStatusFilter} onValueChange={setAlertStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={applyAlertFilter} disabled={alertLoading} className="bg-[#C97A5E] hover:bg-[#B06850]">
                {alertLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-[#FAFAF9] border border-[#E5E5E5]">
              <p className="text-[#A8A8A8] text-xs">Total Alerts</p>
              <p className="text-xl font-bold text-[#2D2420]">{alertHub?.summary?.total || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-100">
              <p className="text-red-600 text-xs">High</p>
              <p className="text-xl font-bold text-red-700">{alertHub?.summary?.by_severity?.high || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-amber-700 text-xs">Medium</p>
              <p className="text-xl font-bold text-amber-800">{alertHub?.summary?.by_severity?.medium || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
              <p className="text-slate-600 text-xs">Open</p>
              <p className="text-xl font-bold text-slate-800">{alertHub?.summary?.by_status?.open || 0}</p>
            </div>
          </div>

          <div className="space-y-2">
            {(alertHub?.items || []).slice(0, 10).map((alert) => (
              <div key={alert.alert_key} className="border border-[#E5E5E5] rounded-lg p-3">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className={cn(
                        'text-xs',
                        alert.severity === 'high' ? 'bg-red-100 text-red-700' : alert.severity === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                      )}>{alert.severity}</Badge>
                      <Badge className="bg-blue-100 text-blue-800 text-xs">{alert.source}</Badge>
                      <Badge className="bg-[#F5EFE9] text-[#5C4033] text-xs">{alert.status}</Badge>
                    </div>
                    <p className="font-semibold text-[#2D2420]">{alert.title}</p>
                    <p className="text-sm text-[#5C4033] mt-1">{alert.message}</p>
                  </div>
                  <div className="flex gap-2">
                    {alert.status !== 'acknowledged' && alert.status !== 'resolved' ? (
                      <Button size="sm" variant="outline" onClick={() => updateAlertStatus(alert.alert_key, 'acknowledged')}>Acknowledge</Button>
                    ) : null}
                    {alert.status !== 'resolved' ? (
                      <Button size="sm" onClick={() => updateAlertStatus(alert.alert_key, 'resolved')} className="bg-[#2A9D8F] hover:bg-[#24897d]">Resolve</Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
            {!(alertHub?.items || []).length && (
              <p className="text-sm text-[#A8A8A8]">No alerts for selected filter.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 3) Demand Forecasting + 4) Territory Performance */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <div className="flex flex-col gap-3">
              <CardTitle className="text-lg text-[#2D2420]">Demand Forecasting</CardTitle>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <Input
                  type="number"
                  min={7}
                  max={365}
                  value={forecastLookbackDays}
                  onChange={(e) => setForecastLookbackDays(e.target.value)}
                  placeholder="Lookback days"
                />
                <Input
                  type="number"
                  min={7}
                  max={180}
                  value={forecastDays}
                  onChange={(e) => setForecastDays(e.target.value)}
                  placeholder="Forecast days"
                />
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={forecastSafetyBuffer}
                  onChange={(e) => setForecastSafetyBuffer(e.target.value)}
                  placeholder="Safety buffer %"
                />
                <Button
                  onClick={applyForecastFilter}
                  disabled={forecastLoading}
                  className="bg-[#C97A5E] hover:bg-[#B06850]"
                >
                  {forecastLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Refresh
                </Button>
              </div>
              <p className="text-xs text-[#A8A8A8]">
                Lookback: {inventoryForecast?.lookback_days || data?.demand_forecasting?.lookback_days || 60}d • Horizon: {inventoryForecast?.forecast_days || data?.demand_forecasting?.forecast_days || 30}d • Buffer: {inventoryForecast?.safety_buffer_pct ?? data?.demand_forecasting?.safety_buffer_pct ?? 10}%
              </p>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#E5E5E5]">
              {(inventoryForecast?.items || data?.demand_forecasting?.items || []).slice(0, 6).map((item) => (
                <div key={item.variant_id} className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <p className="font-medium text-[#2D2420]">{item.invoice_name || item.sku}</p>
                      <p className="text-xs text-[#A8A8A8]">Avg/day: {item.avg_daily_sales} • Stock: {item.current_stock} • Reorder level: {item.reorder_level || 0}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-800">Reorder: {item.suggested_reorder_qty}</Badge>
                  </div>
                </div>
              ))}
              {!(inventoryForecast?.items || data?.demand_forecasting?.items || []).length && (
                <p className="p-6 text-center text-[#A8A8A8]">No demand forecast data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Territory Performance</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#E5E5E5]">
              {(data?.territory_performance?.areas || []).slice(0, 6).map((area) => (
                <div key={area.area} className="p-4 flex justify-between items-start gap-4">
                  <div>
                    <p className="font-medium text-[#2D2420]">{area.area}</p>
                    <p className="text-xs text-[#A8A8A8]">Orders: {area.orders} • Customers: {area.active_customers}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-[#2D2420]">{formatCurrency(area.order_amount)}</p>
                    <p className="text-xs text-[#A8A8A8]">Collected: {formatCurrency(area.collections)}</p>
                  </div>
                </div>
              ))}
              {!(data?.territory_performance?.areas || []).length && (
                <p className="p-6 text-center text-[#A8A8A8]">No territory performance data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Recent Orders</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#E5E5E5]">
              {data?.recent_orders?.length === 0 ? (
                <p className="p-6 text-center text-[#A8A8A8]">No recent orders</p>
              ) : (
                data?.recent_orders?.slice(0, 5).map((order) => (
                  <div key={order.id} className="p-4 hover:bg-[#FAFAF9] transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#2D2420]">{order.order_id}</p>
                        <p className="text-sm text-[#A8A8A8]">{order.customer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#2D2420]">{formatCurrency(order.total_amount)}</p>
                        <Badge className={cn("text-xs", getStatusColor(order.status))}>
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Recent Payments</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#E5E5E5]">
              {data?.recent_payments?.length === 0 ? (
                <p className="p-6 text-center text-[#A8A8A8]">No recent payments</p>
              ) : (
                data?.recent_payments?.slice(0, 5).map((payment) => (
                  <div key={payment.id} className="p-4 hover:bg-[#FAFAF9] transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#2D2420]">{payment.customer_name}</p>
                        <p className="text-sm text-[#A8A8A8]">{payment.payment_method}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#4A7c59]">+{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-[#A8A8A8]">{formatDate(payment.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="bg-white border-[#E5E5E5]">
          <CardHeader className="border-b border-[#E5E5E5]">
            <CardTitle className="text-lg text-[#2D2420]">Leads</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#E5E5E5]">
              {data?.recent_leads?.length === 0 ? (
                <p className="p-6 text-center text-[#A8A8A8]">No recent leads</p>
              ) : (
                data?.recent_leads?.slice(0, 5).map((lead) => (
                  <div key={lead.id} className="p-4 hover:bg-[#FAFAF9] transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#2D2420]">{lead.name}</p>
                        <p className="text-sm text-[#A8A8A8]">{lead.phone}</p>
                      </div>
                      <Badge className={cn("text-xs", getStatusColor(lead.status))}>
                        {lead.status}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
